import { Trans, t } from "@lingui/macro";
import { useMemo, useState } from "react";

import { selectChainId, selectMarketsInfoData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { useTokensFavorites } from "context/TokensFavoritesContext/TokensFavoritesContextProvider";
import { MarketTokensAPRData, getTotalGmInfo, useMarketTokensData } from "domain/synthetics/markets";
import type { MarketsInfoData } from "domain/synthetics/markets/types";
import {
  PerformanceData,
  PerformanceSnapshotsData,
  useGmGlvPerformance,
} from "domain/synthetics/markets/useGmGlvPerformance";
import { convertPoolsTimeRangeToPeriod } from "domain/synthetics/markets/usePoolsTimeRange";
import { PoolsTimeRange } from "domain/synthetics/markets/usePoolsTimeRange";
import { useUserEarnings } from "domain/synthetics/markets/useUserEarnings";
import useWallet from "lib/wallets/useWallet";
import PoolsCard from "pages/Pools/PoolsCard";
import PoolsTimeRangeFilter from "pages/Pools/PoolsTimeRangeFilter";
import { usePoolsIsMobilePage } from "pages/Pools/usePoolsIsMobilePage";

import Loader from "components/Common/Loader";
import Pagination from "components/Pagination/Pagination";
import usePagination, { DEFAULT_PAGE_SIZE } from "components/Referrals/usePagination";
import SearchInput from "components/SearchInput/SearchInput";
import { GMListSkeleton } from "components/Skeleton/Skeleton";
import { Sorter, useSorterHandlers } from "components/Sorter/Sorter";
import { TableTd, TableTh, TableTheadTr, TableTr } from "components/Table/Table";
import { ButtonRowScrollFadeContainer, TableScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";
import Tabs from "components/Tabs/Tabs";

import { FeeApyLabel } from "./FeeApyLabel";
import { GmListItem } from "./GmListItem";
import { GmTokensTotalBalanceInfo } from "./GmTokensTotalBalanceInfo";
import { PerformanceLabel } from "./PerformanceLabel";
import { useFilterSortPools } from "./useFilterSortPools";

export type Props = {
  glvTokensApyData: MarketTokensAPRData | undefined;
  marketsTokensApyData: MarketTokensAPRData | undefined;
  marketsTokensIncentiveAprData: MarketTokensAPRData | undefined;
  glvTokensIncentiveAprData: MarketTokensAPRData | undefined;
  marketsTokensLidoAprData: MarketTokensAPRData | undefined;
  glvPerformance?: PerformanceData | undefined;
  gmPerformance?: PerformanceData | undefined;
  glvPerformanceSnapshots?: PerformanceSnapshotsData | undefined;
  gmPerformanceSnapshots?: PerformanceSnapshotsData | undefined;
  isDeposit: boolean;
  setTimeRange: (timeRange: PoolsTimeRange) => void;
  timeRange: PoolsTimeRange;
};

export type SortField = "price" | "totalSupply" | "wallet" | "apy" | "performance" | "unspecified";

export function GmList({
  marketsTokensApyData,
  marketsTokensIncentiveAprData,
  marketsTokensLidoAprData,
  isDeposit,
  glvPerformance,
  gmPerformance,
  glvPerformanceSnapshots,
  gmPerformanceSnapshots,
  timeRange,
  setTimeRange,
}: Props) {
  const chainId = useSelector(selectChainId);
  const marketsInfo = useSelector(selectMarketsInfoData);

  const { marketTokensData } = useMarketTokensData(chainId, { isDeposit, withGlv: false });
  const { active } = useWallet();
  const userEarnings = useUserEarnings(chainId);
  const { orderBy, direction, getSorterProps } = useSorterHandlers<SortField>("gm-list");
  const [searchText, setSearchText] = useState("");
  const { tab, favoriteTokens, toggleFavoriteToken, setTab } = useTokensFavorites("gm-list");

  const isLoading = !marketsInfo || !marketTokensData;

  const categoryTabs: { value: any; label: string }[] = [
    { value: "all", label: t`All` },
    { value: "favorites", label: t`Favorites` },
    { value: "defi", label: t`DeFi` },
    { value: "meme", label: t`Meme` },
    { value: "rwa", label: t`RWA` },
    { value: "ai", label: t`AI` },
    { value: "layer1", label: t`Layer 1` },
  ];

  const normalizedTab = categoryTabs.some((option) => option.value === tab) ? tab : "all";

  const filteredGmTokens = useFilterSortPools({
    marketsInfo,
    marketTokensData,
    orderBy,
    direction,
    marketsTokensApyData,
    marketsTokensIncentiveAprData,
    marketsTokensLidoAprData,
    searchText,
    tab: normalizedTab,
    favoriteTokens,
    gmPerformance,
  });

  const { currentPage, currentData, pageCount, setCurrentPage } = usePagination(
    `${chainId} ${direction} ${orderBy} ${searchText}`,
    filteredGmTokens,
    DEFAULT_PAGE_SIZE
  );

  const currentPageMarketData = useMemo(() => {
    if (!marketsInfo || currentData.length === 0) {
      return undefined;
    }

    return currentData.reduce((acc, token) => {
      const market = marketsInfo[token.address];
      if (!market) return acc;
      acc[market.marketTokenAddress] = market;
      return acc;
    }, {} as MarketsInfoData);
  }, [marketsInfo, currentData]);

  const currentPageTokenAddresses = useMemo(() => {
    if (!currentPageMarketData) return undefined;

    const addresses = new Set<string>();
    Object.values(currentPageMarketData).forEach((market) => {
      addresses.add(market.marketTokenAddress);
      addresses.add(market.longTokenAddress);
      addresses.add(market.shortTokenAddress);
    });

    return Array.from(addresses);
  }, [currentPageMarketData]);

  const currentPageMarketAddresses = useMemo(() => {
    if (!currentPageMarketData) return undefined;
    return Object.keys(currentPageMarketData);
  }, [currentPageMarketData]);
  const performancePeriod = useMemo(() => convertPoolsTimeRangeToPeriod(timeRange), [timeRange]);

  const {
    gmPerformance: currentPageGmPerformance,
    gmPerformanceSnapshots: currentPageGmPerformanceSnapshots,
    isLoading: isCurrentPageGraphLoading,
  } = useGmGlvPerformance({
    chainId,
    period: performancePeriod,
    gmData: currentPageMarketData,
    glvData: undefined,
    tokenAddresses: currentPageTokenAddresses,
    marketAddresses: currentPageMarketAddresses,
  });

  const resolvedGmPerformance = gmPerformance ?? currentPageGmPerformance;
  const resolvedGmPerformanceSnapshots = gmPerformanceSnapshots ?? currentPageGmPerformanceSnapshots;
  const isGraphLoading = !gmPerformance && !gmPerformanceSnapshots && isCurrentPageGraphLoading;

  const userTotalGmInfo = useMemo(() => {
    if (!active) return;
    return getTotalGmInfo(marketTokensData, marketsInfo);
  }, [marketTokensData, active, marketsInfo]);

  const rows =
    currentData.length > 0 &&
    currentData.map((token) => (
      <GmListItem
        key={token.address}
        token={token}
        marketsTokensApyData={marketsTokensApyData}
        glvTokensIncentiveAprData={undefined}
        marketsTokensIncentiveAprData={marketsTokensIncentiveAprData}
        marketsTokensLidoAprData={marketsTokensLidoAprData}
        glvTokensApyData={undefined}
        glvPerformance={glvPerformance}
        gmPerformance={resolvedGmPerformance}
        glvPerformanceSnapshots={glvPerformanceSnapshots}
        gmPerformanceSnapshots={resolvedGmPerformanceSnapshots}
        isGraphLoading={isGraphLoading}
        isFavorite={favoriteTokens.includes(token.address)}
        onFavoriteClick={toggleFavoriteToken}
      />
    ));

  const isMobile = usePoolsIsMobilePage();

  return (
    <PoolsCard
      // title={t`NTM Pools`}
      // description={t`Pools providing liquidity to specific GMX markets, supporting single-asset and native asset options.`}
      bottom={
        pageCount > 1 ? (
          <Pagination page={currentPage} pageCount={pageCount} onPageChange={setCurrentPage} topMargin={false} />
        ) : undefined
      }
    >
      <div className="flex flex-col gap-16">
        <div className="flex flex-wrap items-center gap-12">
          <SearchInput
            size="s"
            className="*:!text-body-medium"
            value={searchText}
            setValue={setSearchText}
            placeholder={t`Search pools`}
            autoFocus={false}
          />
          <div className="ml-auto max-w-full">
            <PoolsTimeRangeFilter timeRange={timeRange} setTimeRange={setTimeRange} />
          </div>
        </div>
        <div className="max-w-full">
          <ButtonRowScrollFadeContainer>
            <Tabs
              options={categoryTabs}
              selectedValue={normalizedTab}
              onChange={setTab}
              variant="filled"
              className="w-max"
              regularOptionClassname="!px-18"
            />
          </ButtonRowScrollFadeContainer>
        </div>
        {isMobile ? (
          <div className="flex flex-col gap-16">
            {rows}
            {!currentData.length && !isLoading && (
              <div className="text-body-medium text-gray-lighter">
                <Trans>No pools matched.</Trans>
              </div>
            )}

            {isLoading && <Loader />}
          </div>
        ) : (
          <TableScrollFadeContainer>
            <table className="w-[max(100%,820px)]">
              <thead>
                <TableTheadTr bordered>
                  <TableTh className="!pl-0">
                    <Trans>Pool</Trans>
                  </TableTh>
                  <TableTh>
                    <Sorter {...getSorterProps("totalSupply")}>
                      <Trans>TVL</Trans>
                    </Sorter>
                  </TableTh>
                  <TableTh>
                    <Sorter {...getSorterProps("wallet")}>
                      <GmTokensTotalBalanceInfo
                        balance={userTotalGmInfo?.balance}
                        balanceUsd={userTotalGmInfo?.balanceUsd}
                        userEarnings={userEarnings}
                        label={t`Wallet`}
                      />
                    </Sorter>
                  </TableTh>
                  <TableTh>
                    <Sorter {...getSorterProps("apy")}>
                      <FeeApyLabel />
                    </Sorter>
                  </TableTh>
                  <TableTh>
                    <Sorter {...getSorterProps("performance")}>
                      <PerformanceLabel />
                    </Sorter>
                  </TableTh>
                  <TableTh />
                  <TableTh className="!pr-0" />
                </TableTheadTr>
              </thead>
              <tbody>
                {rows}

                {!currentData.length && !isLoading && (
                  <TableTr hoverable={false} bordered={false} className="h-[64.5px]">
                    <TableTd colSpan={7} className="align-top">
                      <div className="text-body-medium text-gray-lighter">
                        <Trans>No pools matched.</Trans>
                      </div>
                    </TableTd>
                  </TableTr>
                )}

                {!isLoading && currentData.length < DEFAULT_PAGE_SIZE && (
                  <GMListSkeleton
                    invisible
                    count={currentData.length === 0 ? DEFAULT_PAGE_SIZE - 1 : DEFAULT_PAGE_SIZE - currentData.length}
                  />
                )}
                {isLoading && <GMListSkeleton />}
              </tbody>
            </table>
          </TableScrollFadeContainer>
        )}
      </div>
    </PoolsCard>
  );
}
